/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { App } from 'antd'
import { CopyOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  API_ACCESS_SUCCESS_MESSAGES,
  buildApiTokenExampleRequest,
} from '@sportninja/common/features/account/apiAccess'
import CopyComponent from 'src/components/CopyComponent'
import Button from 'src/components/ts-components/Button'
import Text from 'src/components/ts-components/Text'
import { formatSuccessToast } from 'src/helpers/formatSuccessToast'
import { API_ACCESS_CARDS } from '../constants'
import { ApiAccessCard } from './ApiAccessCard'

interface ApiQuickStartSectionProps {
  /** The token API's base URL, e.g. `https://host/v1/api-token`. */
  tokenApiBaseUrl: string
}

/**
 * Where a token is used and a first request to try with it, beside the docs
 * link on the tokens card.
 */
export const ApiQuickStartSection = ({
  tokenApiBaseUrl,
}: ApiQuickStartSectionProps) => {
  const { message } = App.useApp()
  const request = buildApiTokenExampleRequest(tokenApiBaseUrl)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(request)
      message.success(
        formatSuccessToast(API_ACCESS_SUCCESS_MESSAGES.exampleRequestCopied)
      )
    } catch {
      message.error('Could not copy the request. Select it and copy instead.')
    }
  }

  return (
    <ApiAccessCard
      title={capitalizeWords(API_ACCESS_CARDS.quickStart.title)}
      subtitle={API_ACCESS_CARDS.quickStart.subtitle}
    >
      <div css={baseUrlStyle}>
        <Text variant='paragraphSmall' color={colors.NEUTRAL_50}>
          {capitalizeWords('Base URL')}
        </Text>
        <CopyComponent
          id={tokenApiBaseUrl}
          customText='URL'
          alternativeBackground
        />
      </div>
      <pre css={snippetStyle}>{request}</pre>
      <div css={footerStyle}>
        <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
          Returns the person the token acts as. Every endpoint is in the API
          docs.
        </Text>
        <Button
          size='compact'
          variant='primary'
          icon={<CopyOutlined />}
          text={capitalizeWords('Copy Example Request')}
          onClick={copy}
        />
      </div>
    </ApiAccessCard>
  )
}

const baseUrlStyle = css`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`

const snippetStyle = css`
  margin: 0;
  padding: 12px;
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid ${colors.SOFT_STEEL};
  background-color: ${colors.SECONDARY_300};
  color: ${colors.WHITE};
  font-family: 'Roboto Mono', ui-monospace, monospace;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre;
`

const footerStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`
